import {
  Badge,
  Box,
  Button,
  Divider,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { DownloadIcon, StarIcon } from "@chakra-ui/icons";
import { WithTranslation, withTranslation } from "react-i18next";
import FileUpload from "./FileUpload";
import { JsonViewer } from "@textea/json-viewer";
import React from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { withColorMode } from "../wrapper/withColorMode";
import { withToast } from "../wrapper/withToast";
import "./Card.scss";

/**
 * Interface for the card arguments.
 *
 * @interface
 * @augments {WithTranslation}
 */
interface CardArguments extends WithTranslation 
{
  /**
   * The image URL.
   *
   * @property {string} imageUrl The image URL.
   */
  imageUrl: string;

  /**
   * A value indicating whether a badge should be shown or not.
   *
   * @property {boolean} showBadge A value indicating whether a badge should be shown or not.
   */
  showBadge: boolean;

  /**
   * The badge text.
   *
   * @property {string} badgeText The badge text.
   */
  badgeText: string;

  /**
   * The credits.
   *
   * @property {number} credits The credits.
   */
  credits: number;

  /**
   * The price.
   *
   * @property {number} price The price.
   */
  price: number;

  /**
   * The title.
   *
   * @property {string} title The title.
   */
  title: string;

  /**
   * The description.
   *
   * @property {string} description The description.
   */
  description: string;

  /**
   * The rating.
   *
   * @property {number} rating The rating.
   */
  rating: number;

  /**
   * The review count.
   *
   * @property {number} reviewcount The review count.
   */
  reviewcount: number;

  /**
   * The endpoint.
   *
   * @property {string} endpoint The endpoint.
   */
  endpoint: string;

  /**
   * The type.
   *
   * @property {string} type The type.
   */
  type: string;

  /**
   * The color mode.
   *
   * @property {any} colorMode The color mode.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  colorMode: any;

  /**
   * The toast.
   *
   * @property {any} toast The toast.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toast: any;
}

/**
 * Interface for the card state.
 *
 * @interface
 */
interface CardState 
{
  /**
   * A value indicating whether a background process has been started or not.
   *
   * @property {boolean} isStarted A value indicating whether a background process has been started or not.
   */
  isStarted: boolean;

  /**
   * A value indicating whether the dialog has been opened or not.
   *
   * @property {boolean} isOpen A value indicating whether the dialog has been opened or not.
   */
  isOpen: boolean;

  /**
   * A value indicating whether a file has been selected or not.
   *
   * @property {boolean} fileSelected A value indicating whether a file has been selected or not.
   */
  fileSelected: boolean;

  /**
   * The result.
   *
   * @property {any} result The result.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;

  /**
   * The text input.
   *
   * @property {string} textInput The text input.
   */
  textInput: string;

  /**
   * The selected file.
   *
   * @property {any} selectedFile The selected file.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedFile: any;
}

/**
 * The card.
 *
 * @class
 * @augments {React.Component<CardArguments, CardState>}
 */
class Card extends React.Component<CardArguments, CardState> 
{
  /**
   * Create an instance of Card.
   *
   * @class
   * @param {CardArguments} props The properties
   * @returns {Card} A Card component instance.
   */
  constructor(props: CardArguments) 
  {
    super(props);

    this.state = {
      isOpen: false,
      isStarted: false,
      textInput: "",
      result: null,
      fileSelected: false,
      selectedFile: null,
    };

    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.renderText = this.renderText.bind(this);
    this.clear = this.clear.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleFileSelected = this.handleFileSelected.bind(this);
    this.startFileBased = this.startFileBased.bind(this);
    this.startUriBased = this.startUriBased.bind(this);
    this.download = this.download.bind(this);
  }

  /**
   * The open event handler.
   *
   * @returns {void}
   */
  onOpen() 
  {
    this.setState({
      isOpen: true,
    });

    if (this.props.type === "link") 
    {
      window.location.href = this.props.endpoint;
    }
  }

  /**
   * The close event handler.
   *
   * @returns {void}
   */
  onClose() 
  {
    this.setState({
      isOpen: false,
    });
  }

  /**
   * Render a text.
   *
   * @returns {JSX.Element} The rendered text.
   */
  renderText() 
  {
    if (!this.state.result) 
    {
      return false;
    }

    return (
      <Box>
        <Divider mt="20px" mb="10px" />
        <Text fontSize="md" as="b" ml="5px" mr="auto" mt={10} mb="30">
          Ergebnis:
        </Text>
        <Box maxH={400} overflow="scroll">
          <JsonViewer
            value={this.state.result}
            theme={
              this.props.colorMode.colorMode === "light" ? "light" : "dark"
            }
          />
        </Box>
      </Box>
    );
  }

  /**
   * Clears the state.
   *
   * @returns {void}
   */
  clear() 
  {
    this.setState({
      result: null,
      isStarted: false,
      fileSelected: false,
      textInput: "",
      selectedFile: null,
    });
  }

  /**
   * Closes the modal.
   *
   * @returns {void}
   */
  closeModal() 
  {
    this.clear();
    this.onClose();
  }

  /**
   * Handle the change event.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event.
   * @returns {void}
   */
  handleChange(event: React.ChangeEvent<HTMLInputElement>) 
  {
    this.setState({
      textInput: event.target.value,
    });
  }

  /**
   * Handle a file selection.
   *
   * @param {any} file The file
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleFileSelected(file: any) 
  {
    this.setState({
      fileSelected: true,
      selectedFile: file,
    });
  }

  /**
   * Handle a run click.
   *
   * @returns {void}
   */
  handleClick() 
  {
    this.setState({
      isStarted: true,
    });

    if (this.state.fileSelected) 
    {
      this.startFileBased(this.state.selectedFile);
    }
    else 
    {
      this.startUriBased(this.state.textInput);
    }
  }

  /**
   * Download the file.
   *
   * @returns {void}
   */
  download() 
  {
    if (this.props.type === "text") 
    {
      const str = JSON.stringify(this.state.result);
      const bytes = new TextEncoder().encode(str);
      const file = new Blob([bytes], {
        type: "application/json;charset=utf-8",
      });
      const element = document.createElement("a");
      element.href = URL.createObjectURL(file);
      element.download = "result.json";
      document.body.appendChild(element);
      element.click();
    }
    else 
    {
      const binaryData = [];
      binaryData.push(this.state.result);
      saveAs(new Blob(binaryData, { type: "model/gltf-binary" }), "result.glb");
    }
  }

  /**
   * Start a file based transaction.
   *
   * @param {string} file The file.
   */
  startFileBased(file: string) 
  {
    const form = new FormData();
    const respo = this.props.type === "file" ? "blob" : "json";
    form.append("File", file);

    axios({
      responseType: respo,
      method: "POST",
      url: this.props.endpoint,
      headers: {
        "Content-Type": "multipart/form-data",
        "x-functions-key":
          "h528gELzUmhaOB8Q7rJfgBpnnGMkarzTxOYs4Hm2cRzvFAx4yoQ04A==",
      },
      data: form,
    })
      .then((response) => 
      {
        this.setState({
          isStarted: false,
          result: response.data,
        });
      })
      .catch((error) => 
      {
        console.error(error);
        this.props.toast({
          title: "Fehler bei Ausführung!",
          description: "Meldung: " + error,
          status: "error",
          duration: null,
          isClosable: true,
        });
        this.clear();
      });
  }

  /**
   * Start a file based transaction.
   *
   * @param {string} url The URL.
   */
  startUriBased(url: string) 
  {
    this.setState({
      isStarted: true,
    });

    const respo = this.props.type === "file" ? "blob" : "json";
    axios({
      responseType: respo,
      method: "GET",
      url: this.props.endpoint,
      headers: {
        "X-functions-key":
          "h528gELzUmhaOB8Q7rJfgBpnnGMkarzTxOYs4Hm2cRzvFAx4yoQ04A==",
      },
      params: {
        link: url,
      },
    })
      .then((response) => 
      {
        console.log(response.data);

        this.setState({
          isStarted: false,
          result: response.data,
        });
      })
      .catch((error) => 
      {
        console.error(error);
        this.props.toast({
          title: "Fehler bei Ausführung!",
          description: "Meldung: " + error,
          status: "error",
          duration: null,
          isClosable: true,
        });
        this.clear();
      });
  }

  /**
   * Render the component.
   *
   * @returns {JSX.Element} The component instance.
   */
  render() 
  {
    return (
      <Box
        as="article"
        maxW="sm"
        minH="lg"
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
      >
        <Image src={this.props.imageUrl} />

        <Box p="6">
          <Box display="flex" alignItems="baseline">
            {this.props.showBadge && (
              <Badge borderRadius="full" px="2" colorScheme="orange">
                {this.props.badgeText}
              </Badge>
            )}
            <Box
              color="gray.500"
              fontWeight="semibold"
              letterSpacing="wide"
              fontSize="xs"
              textTransform="uppercase"
              ml="2"
            >
              {this.props.credits} credit{this.props.credits > 1 && "s"} &bull;{" "}
              {this.props.price} €
            </Box>
          </Box>
          <Box
            mt="1"
            fontWeight="semibold"
            as="h4"
            lineHeight="tight"
            noOfLines={1}
          >
            {this.props.title}
          </Box>
          <Box>
            <Box as="span" color="gray.600" fontSize="xs" whiteSpace="pre-wrap">
              {this.props.description}
            </Box>
          </Box>

          <Box display="flex" mt="2" alignItems="center">
            {Array(5)
              .fill("")
              .map((_, i) => (
                <StarIcon
                  key={i}
                  color={i < this.props.rating ? "orange.500" : "gray.300"}
                />
              ))}
            <Box as="span" ml="1" color="gray.600" fontSize="sm">
              {this.props.reviewcount} Bewertungen
            </Box>
            <Spacer></Spacer>
            <Button
              colorScheme="orange"
              disabled={!this.props.endpoint}
              onClick={this.onOpen}
            >
              Starten
            </Button>
          </Box>
        </Box>

        <Modal isOpen={this.state.isOpen} onClose={this.onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{this.props.title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody alignContent="center">
              {this.props.description}
              {!this.state.result && (
                <Box>
                  <Divider mt="20px" mb="10px" />
                  <FileUpload
                    disabled={this.state.textInput.length > 0}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    handleSelected={(event: any) =>
                      this.handleFileSelected(event)
                    }
                  ></FileUpload>
                  <Box display="flex" textAlign="center">
                    <Text
                      fontSize="md"
                      as="b"
                      ml="auto"
                      mr="auto"
                      mt={10}
                      mb={10}
                    >
                      {" "}
                      - oder -{" "}
                    </Text>
                  </Box>
                  <Input
                    disabled={this.state.fileSelected}
                    onChange={this.handleChange}
                    value={this.state.textInput}
                    placeholder="Link hier einfügen"
                  ></Input>
                </Box>
              )}
              {this.props.type === "text" && this.renderText()}
            </ModalBody>

            <ModalFooter>
              {!this.state.result && (
                <Box>
                  <Button
                    disabled={this.state.isStarted}
                    variant="ghost"
                    onClick={this.closeModal}
                    ml="auto"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={this.handleClick}
                    isLoading={this.state.isStarted}
                    loadingText="Lädt..."
                    disabled={
                      this.state.isStarted
                      || (!this.state.fileSelected
                        && this.state.textInput.length === 0)
                    }
                    colorScheme="orange"
                    ml={3}
                  >
                    Starten
                  </Button>
                </Box>
              )}
              {this.state.result && (
                <Box>
                  <Button
                    leftIcon={<DownloadIcon />}
                    variant="ghost"
                    onClick={this.download}
                    ml="auto"
                  >
                    Download
                  </Button>
                  <Button onClick={this.closeModal} colorScheme="orange" ml={3}>
                    Schließen
                  </Button>
                </Box>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  }
}

export default withTranslation()(withToast(withColorMode(Card)));